import Duck from 'extensible-duck';
import Immutable from 'immutable';
import { normalize, denormalize } from 'normalizr';
import { createSelector } from 'reselect';

import requestsManager from 'lib/requestsManagerAxios'; // eslint-disable-line
import { questionSchema } from '../../schema/programSchema';

export default new Duck({
  namespace: 'organization-config',
  store: 'program',
  types: [
    'CREATE_PROGRAM',
    'CREATE_PROGRAM_PENDING',
    'CREATE_PROGRAM_FULFILLED',
    'CREATE_PROGRAM_REJECTED',
    'CREATE_QUESTION',
    'CREATE_QUESTION_PENDING',
    'CREATE_QUESTION_FULFILLED',
    'CREATE_QUESTION_REJECTED',
    'DELETE_QUESTION',
    'DELETE_QUESTION_PENDING',
    'DELETE_QUESTION_FULFILLED',
    'DELETE_QUESTION_REJECTED',
    'MOVE_QUESTION',
    'MOVE_QUESTION_PENDING',
    'MOVE_QUESTION_FULFILLED',
    'MOVE_QUESTION_REJECTED',
    'PRICE_CHECK',
    'PRICE_CHECK_PENDING',
    'PRICE_CHECK_FULFILLED',
    'PRICE_CHECK_REJECTED',
    'UPDATE_PROGRAM',
    'UPDATE_PROGRAM_PENDING',
    'UPDATE_PROGRAM_FULFILLED',
    'UPDATE_PROGRAM_REJECTED',
    'UPDATE_QUESTION',
    'UPDATE_QUESTION_PENDING',
    'UPDATE_QUESTION_FULFILLED',
    'UPDATE_QUESTION_REJECTED',
    'UPDATE_PROGRAM_JWADMIN',
    'UPDATE_PROGRAM_JWADMIN_PENDING',
    'UPDATE_PROGRAM_JWADMIN_REJECTED',
    'UPDATE_PROGRAM_JWADMIN_FULFILLED',
  ],
  initialState: Immutable.Map({
    // devToolsOn: true,
    priceAfterFees: '$0.00',
    setIsSaving: false,
    $$entities: Immutable.fromJS({
      $$questions: {},
      $$possible_answers: {},
    }),
    $$program: Immutable.fromJS({}),
    $$programError: Immutable.fromJS({}),
    $$questionIds: Immutable.OrderedSet([]),
  }),
  reducer: ($$state, { type, payload, meta }, { types }) => {
    let moveQuestionResultNormalized;
    let updateQuestionEntities;
    switch (type) {
      case types.CREATE_PROGRAM_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.CREATE_PROGRAM_FULFILLED:
        return $$state.merge({
          setIsSaving: false,
          $$programError: Immutable.fromJS({}),
        });
      case types.CREATE_PROGRAM_REJECTED:
        return $$state.merge({
          setIsSaving: false,
          $$programError: Immutable.fromJS(payload.response.data),
        });
      case types.CREATE_QUESTION_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.CREATE_QUESTION_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.CREATE_QUESTION_FULFILLED:
        return $$state
          .update('$$questionIds', $$questionIds =>
            $$questionIds.add(payload.data.id)
          )
          .mergeDeep({
            setIsSaving: false,
            $$entities: Immutable.fromJS(
              normalize(payload.data, questionSchema).entities
            ),
          });
      case types.DELETE_QUESTION_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.DELETE_QUESTION_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.DELETE_QUESTION_FULFILLED:
        return $$state
          .merge({ setIsSaving: false })
          .update('$$questionIds', $$questionIds =>
            $$questionIds.delete(meta.questionId)
          );
      case types.MOVE_QUESTION_PENDING:
        return $$state
          .merge({ setIsSaving: true })
          .set('$$questionIds', Immutable.OrderedSet(meta.newArray));
      case types.MOVE_QUESTION_REJECTED:
        return $$state
          .merge({ setIsSaving: false })
          .set('$$questionIds', Immutable.OrderedSet(meta.oldArray));
      case types.MOVE_QUESTION_FULFILLED:
        moveQuestionResultNormalized = normalize(payload.data, [
          questionSchema,
        ]);
        return $$state
          .set('setIsSaving', false)
          .setIn(
            ['$$entities', '$$questions'],
            Immutable.fromJS(moveQuestionResultNormalized.entities.$$questions)
          )
          .set(
            '$$questionIds',
            Immutable.OrderedSet(moveQuestionResultNormalized.result)
          );
      case types.PRICE_CHECK_FULFILLED:
        return $$state.merge({
          priceAfterFees: payload.data.priceAfterFees,
        });
      case types.PRICE_CHECK_REJECTED:
        return $$state.merge({
          priceAfterFees: '0.00',
        });
      case types.UPDATE_PROGRAM_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_PROGRAM_REJECTED:
        return $$state.merge({
          setIsSaving: false,
          $$programError: Immutable.fromJS(payload.response.data),
        });
      case types.UPDATE_PROGRAM_FULFILLED:
        return $$state.merge({
          setIsSaving: false,
          $$program: Immutable.Map(payload.data.program),
          $$programError: Immutable.fromJS({}),
        });
      case types.UPDATE_PROGRAM_JWADMIN_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_PROGRAM_JWADMIN_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.UPDATE_PROGRAM_JWADMIN_FULFILLED:
        return $$state.merge({
          setIsSaving: false,
          $$program: Immutable.Map(payload.data.program),
        });
      case types.UPDATE_QUESTION_PENDING:
        return $$state.merge({ setIsSaving: true });
      case types.UPDATE_QUESTION_REJECTED:
        return $$state.merge({ setIsSaving: false });
      case types.UPDATE_QUESTION_FULFILLED:
        updateQuestionEntities = normalize(payload.data, questionSchema)
          .entities;
        return $$state
          .merge({ setIsSaving: false })
          .mergeDeepIn(
            ['$$entities', '$$possible_answers'],
            Immutable.fromJS(updateQuestionEntities.$$possible_answers)
          )
          .setIn(
            ['$$entities', '$$questions', `${payload.data.id}`],
            Immutable.fromJS(
              updateQuestionEntities.$$questions[payload.data.id]
            )
          );
      default:
        return $$state;
    }
  },
  selectors: {
    questions: new Duck.Selector(_selectors =>
      createSelector(
        $$state => $$state.getIn(['$$entities', '$$questions']),
        $$state => $$state.getIn(['$$entities', '$$possible_answers']),
        $$state => $$state.get('$$questionIds'),
        ($$questions, $$possible_answers, $$questionIds) => {
          const entities = {
            $$questions: $$questions.toJS(),
            $$possible_answers: $$possible_answers.toJS(),
          };
          return denormalize($$questionIds.toJS(), [questionSchema], entities);
        }
      )
    ),
    stripeStandardAccounts: $$state =>
      $$state.get('$$stripeStandardAccounts').toJS(),
  },
  creators: ({ types }) => ({
    createProgram: registration_program => ({
      type: types.CREATE_PROGRAM,
      payload: requestsManager.post('/admin/programs', {
        registration_program,
      }),
    }),
    createQuestion: (path, registration_custom_question) => ({
      type: types.CREATE_QUESTION,
      payload: requestsManager.post(path, { registration_custom_question }),
    }),
    deleteQuestion: questionId => ({
      type: types.DELETE_QUESTION,
      payload: requestsManager.delete(
        `/admin/registration_custom_questions/${questionId}`
      ),
      meta: { questionId },
    }),
    moveQuestion: (questionId, position, newArray, oldArray) => ({
      type: types.MOVE_QUESTION,
      payload: requestsManager.post(
        `/admin/registration_custom_questions/${questionId}/move`,
        { position }
      ),
      meta: { newArray, oldArray },
    }),
    priceCheck: (price, stripeStandardAccountId) => ({
      type: types.PRICE_CHECK,
      payload: requestsManager.get(
        `/admin/organization/program_price_after_fees.json?price=${price}&stripe_standard_account_id=${stripeStandardAccountId}`
      ),
    }),
    updateProgram: registration_program => ({
      type: types.UPDATE_PROGRAM,
      payload: requestsManager.patch(
        `/admin/programs/${registration_program.id}`,
        { registration_program }
      ),
    }),
    updateStripeAccount: (
      registration_program_id,
      stripe_standard_account_id
    ) => ({
      type: types.UPDATE_PROGRAM_JWADMIN,
      payload: requestsManager.patch(
        `/jwadmin/programs/${registration_program_id}`,
        { registration_program: { stripe_standard_account_id } }
      ),
    }),
    updateQuestion: registration_custom_question => ({
      type: types.UPDATE_QUESTION,
      payload: requestsManager.patch(
        `/admin/registration_custom_questions/${
          registration_custom_question.id
        }`,
        { registration_custom_question }
      ),
    }),
  }),
});
